import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Pagination, Row, Table } from "react-bootstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import { Loader, ScrollToTop, SearchBar } from "../../../components";

const formatTanggal = (value) =>
  value ? new Date(value).toLocaleString("id-ID") : "-";

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getProductImage = (barang) =>
  barang?.barangfotos?.[0]?.linkGambar ||
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f4f4f4'/%3E%3Ctext x='60' y='64' text-anchor='middle' font-family='Arial' font-size='13' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

const TampilPesanan = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [pesanans, setPesanans] = useState([]);
  const [selectedPesanan, setSelectedPesanan] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        dispatch({ type: "LOGOUT" });
        navigate("/");
      }
    },
    [dispatch, navigate]
  );

  const getPesanans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/pesanansPagination?search_query=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        }
      );
      setPesanans(response.data.pesanans || []);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data pesanan.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  useEffect(() => {
    getPesanans();
  }, [getPesanans]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedPesanan(null);
      return;
    }

    const foundPesanan = pesanans.find(
      (pesanan) => String(pesanan.id) === String(selectedId)
    );
    setSelectedPesanan(foundPesanan || null);
  }, [pesanans, selectedId]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Transaksi</div>
          <h1 className="page-title">Pesanan</h1>
        </div>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedPesanan && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button
                className="btn-back"
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate("/pesanan")}
              >
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail Pesanan</h2>
                <div className="text-muted">{selectedPesanan.nomorOrder}</div>
              </div>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Nomor Order</div>
                <div className="detail-value">{selectedPesanan.nomorOrder || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Kode User Ecommerce</div>
                <div className="detail-value">{selectedPesanan.visitorId || "-"}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Tanggal/Jam Dibuat</div>
                <div className="detail-value">{formatTanggal(selectedPesanan.createdAt)}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Status</div>
                <div className="detail-value">{selectedPesanan.status || "-"}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Nama Customer</div>
                <div className="detail-value">{selectedPesanan.nama || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Nomor Handphone</div>
                <div className="detail-value">{selectedPesanan.nomorHandphone || "-"}</div>
              </div>
            </Col>
            <Col md={12}>
              <div className="mb-3">
                <div className="detail-label">Alamat Pengiriman</div>
                <div className="detail-value">{selectedPesanan.alamatPengiriman || "-"}</div>
              </div>
            </Col>
          </Row>
          <Table responsive className="data-table mt-3">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Barang</th>
                <th>Qty</th>
                <th>Harga</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(selectedPesanan.items || []).map((item) => (
                <tr key={item.id}>
                  <td>
                    <img
                      src={getProductImage(item.barang)}
                      alt={item.namaBarang || "Barang"}
                      style={{ width: 58, height: 58, objectFit: "cover", borderRadius: 6 }}
                    />
                  </td>
                  <td>{item.namaBarang || "-"}</td>
                  <td>{item.qty}</td>
                  <td>{formatRupiah(item.harga)}</td>
                  <td>{formatRupiah(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="text-end fw-bold mt-3">
            Total Pesanan: {formatRupiah(selectedPesanan.totalHarga)}
          </div>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <section className="table-panel">
        <Table hover responsive className="data-table">
          <thead>
            <tr>
              <th>Nomor Order</th>
              <th>Kode User Ecommerce</th>
              <th>Nama</th>
              <th>Tanggal/Jam Dibuat</th>
              <th>Status</th>
              <th>Total Harga</th>
            </tr>
          </thead>
          <tbody>
            {pesanans.map((pesanan) => (
              <tr
                key={pesanan.id}
                className={`row-clickable ${
                  String(pesanan.id) === String(selectedId) ? "active-row" : ""
                }`}
                onClick={() => navigate(`/pesanan/${pesanan.id}`)}
              >
                <td>{pesanan.nomorOrder}</td>
                <td>{pesanan.visitorId}</td>
                <td>{pesanan.nama}</td>
                <td>{formatTanggal(pesanan.createdAt)}</td>
                <td>{pesanan.status}</td>
                <td>{formatRupiah(pesanan.totalHarga)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev disabled={page === 0} onClick={() => setPage(page - 1)} />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item key={index} active={index === page} onClick={() => setPage(index)}>
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next disabled={page + 1 >= totalPage} onClick={() => setPage(page + 1)} />
        </Pagination>
      )}
    </div>
  );
};

export default TampilPesanan;
